import { useState } from "react";
import { Icon } from "@/components/ui/Icon";
import { useHomePref } from "@/hooks/useHomePref";
import { useSpace } from "@/hooks/useSpace";
import { useToast } from "@/hooks/useToast";
import { bizHomeLabel, homePrefFromKey, homePrefKey, selectableTenantHomes, type HomePref } from "@/domain/home_pref";
import { SPACE_META } from "@/domain/space";
import { homePrefRepo } from "@/services/home_pref.repository";

/**
 * HomeTab — 내 계정·환경 › 첫 화면(2026-11-02).
 *  로그인 직후·앱을 새로 열었을 때(`/`) 가는 화면을 고른다. 라디오를 고르면 바로 저장(저장 바 없음, NotifyTab 과 같음).
 *  법인 홈은 내 소속 + 사업 권한이 있는 것만 보인다(domain/home_pref.ts selectableTenantHomes).
 *  링크로 특정 화면에 들어온 경우(/c/…/sales 등)는 이 설정과 무관하게 그 화면이 열린다.
 */
interface Option {
  pref: HomePref;
  label: string;
  desc: string;
  tenantKey?: string;
}

export function HomeTab() {
  const { pref, loaded } = useHomePref();
  const { tenants, myBizs, hasPermIn } = useSpace();
  const toast = useToast();
  const [saving, setSaving] = useState<string | null>(null);

  const options: Option[] = [
    { pref: { kind: "my" }, label: "내 업무", desc: "일정·할 일·내 급여 — 기본값" },
    ...selectableTenantHomes({ tenants, myBizs, hasPermIn }).map((h) => ({
      pref: { kind: "tenant" as const, tenant: h.tenant, biz: h.biz },
      label: `${SPACE_META[h.tenant].label} · ${bizHomeLabel(h.biz)}`,
      desc: "그 사업의 대시보드",
      tenantKey: h.tenant,
    })),
    { pref: { kind: "last" }, label: "마지막에 본 공간", desc: "이 기기에서 지난번에 보던 내 업무 또는 법인 홈" },
  ];
  const current = homePrefKey(pref);

  async function choose(key: string) {
    const next = homePrefFromKey(key);
    if (!next || key === current || saving) return;
    setSaving(key);
    try {
      await homePrefRepo.save(next);
      toast.success("첫 화면을 바꿨습니다. 다음 로그인부터 적용됩니다.");
    } catch (e) {
      toast.error(`저장하지 못했습니다: ${(e as Error).message}`);
    } finally {
      setSaving(null);
    }
  }

  return (
    <div className="ma-pane">
      <section className="ma-sec">
        <div className="ma-sec-hd">
          <h3 className="ma-sec-title">로그인 후 첫 화면</h3>
          <span className="ma-help">앱을 새로 열 때도 같은 화면으로 갑니다</span>
        </div>
        {!loaded ? (
          <div className="ma-skel-list" aria-busy="true">
            <span className="ma-skel" />
            <span className="ma-skel" />
          </div>
        ) : (
          <div className="ma-limit-list" role="radiogroup" aria-label="로그인 후 첫 화면">
            {options.map((o) => {
              const key = homePrefKey(o.pref);
              const on = key === current;
              return (
                <label key={key} className={`ma-limit-row${on ? " is-on" : ""}`}>
                  <input
                    type="radio"
                    name="ma-home-pick"
                    checked={on}
                    disabled={saving !== null}
                    onChange={() => void choose(key)}
                  />
                  <span className="ma-limit-row-text">
                    <span className={`ma-limit-row-name${o.tenantKey ? ` ma-home-${o.tenantKey}` : ""}`}>{o.label}</span>
                    <span className="ma-help">{o.desc}</span>
                  </span>
                </label>
              );
            })}
          </div>
        )}
        <div className="ma-note">
          <Icon name="info" size={16} />
          <span>링크로 특정 화면에 들어온 경우에는 이 설정과 상관없이 그 화면이 열립니다.</span>
        </div>
      </section>
    </div>
  );
}
